import React, { ChangeEvent, useEffect, useRef } from "react";

import {
    ActionsWrapper,
    AlertIcon,
    AlertWrapper,
    InputField,
    StyledIcon,
    ContentWrapper,
    Text,
    Wrapper,
} from "./StyledComponents";

export interface InputWithActionsProps {
    "data-testid"?: string;
    isEditing: boolean;
    setIsEditing: (value: boolean) => void;
    disabled?: boolean;
    currentText: string;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onSave: () => void;
    onDiscard: () => void;
    onDelete?: () => void;
    placeholder?: string;
    maxLength?: number;
    showWarning?: boolean;
    isSaveButtonDisabled?: boolean;
    triggerFlash?: boolean;
    iconsize?: string;
    fontSize?: string;
    underlined?: boolean;
}

const InputWithActions: React.FC<InputWithActionsProps> = ({ "data-testid": testid, ...props }) => {
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (props.isEditing) {
            inputRef.current?.focus();
        }
    }, [props.isEditing]);

    function handleKeyDown(e: React.KeyboardEvent) {
        if (e.key === "Enter") {
            e.preventDefault();
            props.onSave();
        } else if (e.key === "Escape") {
            e.preventDefault();
            props.onDiscard();
        }
    }

    function handleEdit(e: React.MouseEvent) {
        e.stopPropagation();

        if (!props.isEditing && !props.disabled) {
            e.preventDefault();
            props.setIsEditing(true);
        }
    }

    function handleSave(e: React.MouseEvent) {
        props.isSaveButtonDisabled ? e.preventDefault() : props.onSave();
    }

    function handleDelete(e: React.MouseEvent) {
        e.preventDefault();
        e.stopPropagation();
        if (props.onDelete && !props.disabled) {
            props.onDelete();
        }
    }

    const renderContent = () => {
        if (props.isEditing) {
            return (
                <InputField
                    data-testid={testid ?? "input-field-input-with-actions"}
                    type={"text"}
                    value={props.currentText ?? ""}
                    onChange={props.onChange}
                    onKeyDown={handleKeyDown}
                    onBlur={props.onSave}
                    ref={inputRef}
                    placeholder={props.placeholder}
                    maxLength={props.maxLength}
                    fontSize={props.fontSize}
                    disabled={props.disabled}
                />
            );
        } else {
            return (
                <AlertWrapper disabled={props.disabled}>
                    {props.showWarning && !props.currentText && <AlertIcon name="alert" />}
                    <Text
                        fontSize={props.fontSize}
                        lightGrey={props.underlined && props.currentText === ""}
                    >
                        {props.currentText || props.placeholder}
                    </Text>
                </AlertWrapper>
            );
        }
    };

    const renderClickableIcons = () => {
        return (
            <ActionsWrapper>
                {props.isEditing ? (
                    <>
                        <StyledIcon
                            name={"exercise-ok"}
                            onMouseDown={handleSave}
                            disabled={props.isSaveButtonDisabled || props.disabled}
                            iconsize={props.iconsize}
                        />
                        <StyledIcon
                            name={"close"}
                            onMouseDown={props.onDiscard}
                            iconsize={props.iconsize}
                            disabled={props.disabled}
                        />
                    </>
                ) : (
                    <>
                        <StyledIcon
                            name={"edit-avatar"}
                            onMouseDown={handleEdit}
                            iconsize={props.iconsize}
                            disabled={props.disabled}
                        />
                        {props.onDelete && props.currentText !== "" && (
                            <StyledIcon
                                name={"trash"}
                                onMouseDown={handleDelete}
                                iconsize={props.iconsize}
                                disabled={props.disabled}
                            />
                        )}
                    </>
                )}
            </ActionsWrapper>
        );
    };

    return (
        <Wrapper
            onMouseDown={handleEdit}
            clickable={!props.isEditing}
            underlined={props.underlined}
            disabled={props.disabled}
        >
            <ContentWrapper
                isFocused={props.isEditing}
                highlighted={props.showWarning}
                underlined={props.underlined}
                isFilledIn={props.currentText !== ""}
                className={props.triggerFlash ? "triggerFlash" : ""}
            >
                {renderContent()}
            </ContentWrapper>

            {renderClickableIcons()}
        </Wrapper>
    );
};

export default InputWithActions;
